import { useQuery } from "@apollo/client";

import {
  Chip,
  Container,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";

import ErrorCard from "src/components/ErrorCard";
import LoadingIndicator from "src/components/LoadingIndicator";
import PageTitle from "src/components/PageTitle";
import { getDataDictionary } from "src/graphql/generated/getDataDictionary";
import { GET_DATA_DICTIONARY } from "src/graphql/tenantExportConfigs";

const useStyles = makeStyles(theme => ({
  tableSection: {
    marginBottom: theme.spacing(8)
  },
  tableName: {
    fontFamily: "monospace"
  },
  columnName: {
    fontFamily: "monospace"
  },
  columnNameCell: {
    width: "300px"
  },
  columnTypeCell: {
    width: "125px"
  },
  columnNullableCell: {
    width: "50px"
  },
  tableChip: {
    marginLeft: theme.spacing(1)
  },
  columnChip: {
    marginRight: theme.spacing(1)
  }
}));

export default function DataDictionary(): JSX.Element {
  const classes = useStyles();
  const { loading, error, data } =
    useQuery<getDataDictionary>(GET_DATA_DICTIONARY);

  if (loading) {
    return <LoadingIndicator />;
  }

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  return (
    <Container>
      <PageTitle title="Dialer Exports Data Dictionary" />
      {data!.getDataDictionary.tables.map(table => (
        <div key={table.name} className={classes.tableSection}>
          <h2>
            <span className={classes.tableName}>{table.name}</span>
            {table.deprecated && (
              <Chip
                className={classes.tableChip}
                label="Deprecated"
                color="secondary"
              />
            )}
          </h2>
          <p>{table.description}</p>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.columnNameCell}>Column</TableCell>
                <TableCell className={classes.columnTypeCell}>Type</TableCell>
                <TableCell className={classes.columnNullableCell}>
                  Nullable
                </TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {table.columns.map(column => (
                <TableRow key={column.name}>
                  <TableCell>
                    <span className={classes.columnName}>{column.name}</span>
                  </TableCell>
                  <TableCell>{column.dataType}</TableCell>
                  <TableCell>{column.nullable ? "Yes" : "No"}</TableCell>
                  <TableCell>
                    {column.deprecated && (
                      <Chip
                        className={classes.columnChip}
                        label="Deprecated"
                        color="secondary"
                        size="small"
                      />
                    )}
                    {column.description}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ))}
    </Container>
  );
}
